// Libs
import classNames from 'classnames/bind';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  createFilterOptions,
  FormHelperText,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
// Others
import { varColor, varTypography } from '~/themes/themeVariables';
import { IBaseAutoComplete } from '~/utils/interface/common';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, ENTER_KEY, SPACE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseAutocomplete.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  id?: string;
  label?: string;
  options: ReadonlyArray<T>;
  multiple?: boolean;
  required?: boolean;
  errorMessage?: string;
  value?: string | string[] | null;
  maxItem?: number;
  onChange?: (newValue: T | T[] | null) => void;
  onAddNewOption?: (newValue: T) => void;
};

const cx = classNames.bind(styles);

const BaseAutocomplete = <T extends IBaseAutoComplete>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    id,
    label,
    options,
    required,
    multiple = true,
    maxItem = 5,
    errorMessage,
    value,
    onChange,
    onAddNewOption,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const filter = createFilterOptions<T>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [autocompleteValue, setAutocompleteValue] = useState<T | T[] | null>(multiple ? [] : null);
  const [inputValue, setInputValue] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const convertedValue = convertValueToOptions(value);
    setAutocompleteValue(convertedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options, multiple]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: T | T[] | null) => {
    if (!newValue) {
      setAutocompleteValue(multiple ? [] : null);
      onChange && onChange(multiple ? [] : null);
      return;
    }

    if (Array.isArray(newValue) && newValue.length > maxItem) {
      return;
    }

    const isNewOption = (option: T) => !options.some((o) => o.value === option.value);

    if (multiple && Array.isArray(newValue)) {
      const newItems = newValue.filter(isNewOption);
      if (newItems.length && onAddNewOption) {
        newItems.forEach((item) => onAddNewOption?.(item));
      }
    } else if (!multiple && newValue && isNewOption(newValue as T)) {
      onAddNewOption?.(newValue as T);
    }
    setAutocompleteValue(newValue);
    onChange && onChange(newValue);
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setInputValue(value);
  };

  const handleAddItem = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === SPACE) {
      event.stopPropagation();
    }

    if (event.key === ENTER_KEY) {
      event.preventDefault();
    }
  };

  const convertValueToOptions = (value?: string | string[] | null): T | T[] | null => {
    if (!value) return multiple ? [] : null;

    if (multiple && Array.isArray(value)) {
      return value.map((val) => {
        const existingOption = options.find((option) => option.value === val);
        return existingOption || ({ value: val, label: val } as T);
      });
    } else if (!multiple && typeof value === 'string') {
      const existingOption = options.find((option) => option.value === value);
      return existingOption || ({ value, label: value } as T);
    } else if (!multiple && Array.isArray(value) && value.length > DEFAULT_NUMBER_ZERO) {
      const firstValue = value[DEFAULT_NUMBER_ZERO];
      const existingOption = options.find((option) => option.value === firstValue);
      return existingOption || ({ value: firstValue, label: firstValue } as T);
    } else if (multiple && typeof value === 'string') {
      const existingOption = options.find((option) => option.value === value);
      return [existingOption || ({ value: value, label: value } as T)];
    }

    return multiple ? [] : null;
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseAutocompleteComponent' className={cx('container')}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Autocomplete
        {...(multiple && { disableCloseOnSelect: true, filterSelectedOptions: true })}
        multiple={multiple}
        size='small'
        className={cx('autocomplete')}
        sx={{
          '.MuiInputBase-sizeSmall': {
            gap: '4px',
            '.MuiAutocomplete-input': {
              minHeight: '0px !important',
            },
          },

          '.MuiAutocomplete-endAdornment': {
            right: '16px !important',
          },
        }}
        id={id || 'autocomplete'}
        options={options || []}
        getOptionLabel={(option: T) => String(option.label)}
        value={autocompleteValue}
        onChange={handleAutocompleteChange}
        inputValue={inputValue}
        onKeyDown={(event) => handleAddItem(event)}
        onInputChange={handleInputChange}
        slotProps={{
          paper: {
            className: cx(
              'paper',
              Array.isArray(autocompleteValue) && autocompleteValue.length >= maxItem && 'optionDisable'
            ),
          },
          listbox: {
            className: cx('listBox'),
            sx: {
              '.MuiAutocomplete-option': {
                p: '8px',
                minHeight: '35px',
                borderRadius: '6px',
                fontSize: varTypography.fontSize['12'],
                color: varColor.secondary,
              },
            },
          },
          chip: {
            className: cx('chip'),
          },
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (Array.isArray(autocompleteValue) && autocompleteValue.length >= maxItem) {
            return filtered;
          }

          const { inputValue } = params;

          const isExisting = options.some(
            (option) => inputValue.toLowerCase() === option.label.toLowerCase()
          );
          if (inputValue !== EMPTY_STRING && !isExisting) {
            filtered.push({
              label: inputValue,
              value: inputValue,
            } as T);
          }

          return filtered;
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box component='li' key={option.value} {...rest}>
              {!options.some((o) => o.value === option.value) && (
                <Box
                  sx={{
                    marginRight: '8px',
                  }}
                >
                  <img className={cx('iconPlus')} src={icons.activityIconCreate} alt={t('common_alt_img')} />
                </Box>
              )}
              {option.label}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className={cx('textField')}
            sx={{
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                pl: '12px',
                minHeight: '35px',
              },
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                p: 0,
                minHeight: '30px',
              },
              '.MuiOutlinedInput-root': {
                fontSize: varTypography.fontSize['12'],
                borderRadius: '9px',
                gap: '6px',
              },
              '.MuiAutocomplete-input': {
                color: varColor.textPlaceholderField,
              },
              '.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                right: '12px',
              },
            }}
            placeholder={t('common_search_placeholder')}
          />
        )}
        popupIcon={
          <img src={icons.commonIconArrowDown} alt={t('common_alt_img')} className={cx('arrowIcon')} />
        }
        noOptionsText={<Typography className={cx('noOptions')}>{t('common_no_data_available')}</Typography>}
      />

      {errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};

export default BaseAutocomplete;
